import type { ConfigLayer } from '@Map/panel/types';

/**
 * Walks the panel config and collects the ids of every hidden leaf layer, so
 * the caller can push that set into `EsriLayerManager.setLayerVisibility()`.
 *
 * `EsriLayerManager` only knows leaf layer ids (groups are panel-only), so a
 * node with `visible: false` propagates "hidden" to every leaf below it.
 *
 * Only `node.layers` (real nested groups) makes a node a non-leaf here.
 * `virtualLayers` are panel-display-only rows for a colored-by-attribute/
 * range layer's bands (`ConfigAdaptor._processForPanel`) — they're never
 * registered in `EsriLayerManager`, so recursing into them instead of
 * pushing the node's own id would silently drop that id from the result,
 * leaving the layer force-shown regardless of its toggle state.
 */
export function computeHiddenLeafIds(nodes: ConfigLayer[]): string[] {
	const hiddenLeafIds: string[] = [];
	const collect = (layers: ConfigLayer[], inheritedHidden: boolean) => {
		for (const node of layers) {
			const nodeHidden = inheritedHidden || node.visible === false;
			if (node.layers && node.layers.length > 0) {
				collect(node.layers, nodeHidden);
			} else if (nodeHidden && node.id) {
				hiddenLeafIds.push(node.id);
			}
		}
	};
	collect(nodes, false);
	return hiddenLeafIds;
}
